export default defineNuxtRouteMiddleware(async (to) => {
  const localePath = useLocalePath();
  const businessStore = useBusinessStore();

  const { isLoggedIn } = useAuth();
  const { getBusiness } = useBusiness();
  const {
    business,
    default_js_formats,
    cartCount,
    cartCountLocation,
    selectedLocation,
    locationStatusData,
  } = storeToRefs(businessStore);
  const { getLocationDetails } = useGetRequests();

  const locationIdentifier = to.params.location_identifier as string;
  const isSameLocation = selectedLocation.value?.identifier === locationIdentifier;
  const isKnownClosed = locationStatusData.value.identifier === locationIdentifier;

  if ((!business.value || !isSameLocation) && !isKnownClosed) {
    try {
      const response = await getBusiness(locationIdentifier);
      if (!response) {
        // A 200 with nothing in it is a broken answer, not a missing salon,
        // and it carries no status of its own to pass on.
        throw createError({ statusCode: 503 });
      }
      if (response?.data?.location?.status?.toLowerCase() === "closed") {
        const { name, status, identifier } = response.data.location;
        const close_message = (
          response.data.location as { close_message?: string }
        ).close_message;
        locationStatusData.value = {
          name: name || "",
          status: status || "",
          close_message: close_message || "",
          identifier: identifier || "",
        };
        return navigateTo(localePath(`/${locationIdentifier}/closed`));
      } else {
        selectedLocation.value = response?.data?.location;
        business.value = response?.data?.business;
        default_js_formats.value = response?.data?.business
          ?.default_js_datetime_format as any; // eslint-disable-line @typescript-eslint/no-explicit-any

        useServiceGender().setHasMixedSections(
          !!response?.data?.location?.has_mixed_sections,
        );
      }
    } catch (error: unknown) {
      console.error("Error fetching business data:", error);

      /**
       * Pass the API's own status through. This used to answer every failure
       * with 404, so the API throttling us, a 5xx or a timeout all told the
       * visitor the salon does not exist — and invited search engines to drop
       * storefronts that are perfectly fine.
       *
       * A request that never got an answer (timeout, DNS, refused connection)
       * carries no status; 503 is the one we have to supply ourselves, and it
       * is the honest reading of it.
       */
      throw createError({ statusCode: httpErrorStatus(error) ?? 503 });
    }
  }

  useUpdateLocation(to);

  if (business.value?.is_freemium) {
    const indexPath = localePath(`/${locationIdentifier}`);
    const normalizedTo = to.path.replace(/\/+$/, "") || "/";
    const normalizedIndex = indexPath.replace(/\/+$/, "") || "/";
    if (normalizedTo !== normalizedIndex) {
      return navigateTo(indexPath);
    }
  }

  /**
   * Products can only be bought where a payment method allows it — the same
   * test the menu uses to show the products link and the cart badge. Nothing
   * reads the cart count otherwise, so it is not worth a request.
   */
  const canBuyProducts = !!business.value?.payment_methods?.some(
    (method) => method?.can_buy_product,
  );

  /**
   * The cart count is loaded once per location. It used to refetch whenever the
   * count was 0, but 0 is a real count — an empty cart — so that refetched on
   * every navigation and never settled. Tracking which location it was loaded
   * for also keeps query-only navigations (a page switching its own mode, say)
   * from firing a second request for something that cannot have changed.
   */
  if (
    business.value &&
    canBuyProducts &&
    selectedLocation.value?.status?.toLowerCase() !== "closed" &&
    isLoggedIn.value &&
    cartCountLocation.value !== locationIdentifier
  ) {
    try {
      const response = await getLocationDetails(locationIdentifier);
      cartCount.value = response.data.cart_count;
      cartCountLocation.value = locationIdentifier;
    } catch (err: unknown) {
      /**
       * Now that `getLocationDetails` actually rejects, this branch runs — and
       * it must not move the visitor. The count is a badge; the old redirect to
       * the location index would, on the location index itself, send the router
       * to the route it is still resolving.
       *
       * `$larafetch` has already surfaced the error. Leaving
       * `cartCountLocation` unset is deliberate: the next navigation retries.
       */
      console.error("Error fetching location details:", err);
    }
  }
});
